import { useState, useLayoutEffect, useRef, useCallback } from 'react';

/**
 * Measures an element and re-measures it whenever it resizes.
 *
 * The render-time measurement is kept because it is what makes the first paint
 * correct, but on its own it only notices a resize that happens to coincide
 * with a re-render. A ResizeObserver covers the rest: a container that changes
 * width without this component re-rendering (a sibling panel opening, the host
 * page reflowing) now reports the new size instead of a stale one.
 *
 * State is written only when a value actually changes, and the returned object
 * keeps its identity while the size holds. Unconditional writes would re-render
 * on every observer callback and, where the measurement feeds back into layout,
 * risk a ResizeObserver loop.
 *
 * @param {string} [propName] Read the element from this prop of the ref target.
 * @param {'inner'|'outer'} [mode] `inner` = clientWidth/Height (content box),
 *                                 `outer` = bounding rect (border box).
 * @returns {[object, {height: number, width: number}]}
 */
export default (
  propName /* in case el is not a toplevel prop of current */,
  mode = 'inner',
) => {
  const [dimensions, setDimensions] = useState({ height: 0, width: 0 });
  const ref = useRef(null);

  const resolveEl = useCallback(() => {
    if (!ref.current) return null;
    return propName ? ref.current[propName] : ref.current;
  }, [propName]);

  const measure = useCallback(() => {
    const el = resolveEl();
    if (!el) return;

    let next;
    if (window.getComputedStyle(el).display === 'none') {
      next = { height: 0, width: 0 };
    } else if (mode === 'outer') {
      const rect = el.getBoundingClientRect();
      next = { height: rect.height, width: rect.width };
    } else if (mode === 'inner') {
      next = { height: el.clientHeight, width: el.clientWidth };
    } else {
      throw 'mode prop only accepts "inner" or "outer" as values';
    }

    setDimensions((current) =>
      current.height === next.height && current.width === next.width
        ? current
        : next,
    );
  }, [resolveEl, mode]);

  useLayoutEffect(() => {
    measure();
  });

  useLayoutEffect(() => {
    const el = resolveEl();
    if (!el) return undefined;

    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [resolveEl, measure]);

  return [ref, dimensions];
};
